import { cantonFixStatusSchema, type CantonFixStatus } from '@flux/shared';
import { X } from 'lucide-react';
import type { JSX } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button.js';
import { SegmentedRadioGroup, type SegmentedOption } from '../settings/recharge-parts.js';
import { PeriodFilter } from './filter-controls.js';

/**
 * Filtres de l'onglet Cantons suisses (`06` §8.2) : un statut à la fois
 * (contrôle segmenté) et la période.
 *
 * Le statut a un **défaut calculé** : « À traiter » s'il existe des adresses à
 * traiter, sinon « Toutes ». Ce défaut n'est jamais écrit dans l'URL : `status`
 * absent = défaut ; `status=[]` = « Toutes » choisi explicitement ; `[code]` =
 * ce statut.
 */

/** Choix du contrôle segmenté : un statut, ou toutes les corrections. */
export type CantonStatusChoice = CantonFixStatus | 'all';

export const CANTON_STATUS_OPTIONS: readonly SegmentedOption<CantonStatusChoice>[] = [
  { value: 'unresolved', label: 'À traiter' },
  { value: 'updated', label: 'Corrigées' },
  { value: 'simulated', label: 'Simulées' },
  { value: 'resolved_manually', label: 'Résolues à la main' },
  { value: 'error', label: 'Erreurs' },
  { value: 'all', label: 'Toutes' },
];

export interface CantonFixFiltersValue {
  /** `undefined` tant que l'utilisateur n'a rien choisi (défaut calculé). */
  status: CantonStatusChoice | undefined;
  /** ISO-8601 UTC, début du jour parisien choisi. */
  from?: string;
  /** ISO-8601 UTC, fin du jour parisien choisi. */
  to?: string;
}

export type CantonFixFiltersPatch = Partial<{
  status: CantonFixStatus[] | undefined;
  from: string | undefined;
  to: string | undefined;
}>;

/** Patch qui revient au défaut (« Réinitialiser »). */
export const CLEAR_CANTON_FIX_FILTERS: CantonFixFiltersPatch = {
  status: undefined,
  from: undefined,
  to: undefined,
};

function readRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

const isoDateTime = z.iso.datetime();
/** Un statut au plus : le contrôle est à choix unique. */
const statusParam = z.array(cantonFixStatusSchema).max(1);

/** Lit les filtres de l'onglet depuis `useSearch({strict:false})`. */
export function readCantonFixFilters(rawSearch: unknown): CantonFixFiltersValue {
  const search = readRecord(rawSearch);
  const status = statusParam.safeParse(search.status);
  const from = isoDateTime.safeParse(search.from);
  const to = isoDateTime.safeParse(search.to);
  return {
    status: status.success ? (status.data[0] ?? 'all') : undefined,
    ...(from.success ? { from: from.data } : {}),
    ...(to.success ? { to: to.data } : {}),
  };
}

/** Choix affiché et demandé au serveur : celui de l'URL, sinon le défaut calculé. */
export function effectiveCantonStatus(
  value: CantonFixFiltersValue,
  unresolvedCount: number,
): CantonStatusChoice {
  return value.status ?? (unresolvedCount > 0 ? 'unresolved' : 'all');
}

export function hasPeriodFilter(value: CantonFixFiltersValue): boolean {
  return value.from !== undefined || value.to !== undefined;
}

export interface CantonFixFiltersProps {
  value: CantonFixFiltersValue;
  /** Choix effectif (défaut calculé inclus). */
  status: CantonStatusChoice;
  onChange: (patch: CantonFixFiltersPatch) => void;
}

export function CantonFixFilters({ value, status, onChange }: CantonFixFiltersProps): JSX.Element {
  const canReset = value.status !== undefined || hasPeriodFilter(value);
  return (
    <div className="flex w-full flex-wrap items-center gap-2">
      <SegmentedRadioGroup
        name="canton-fix-status"
        legend="Filtrer par statut"
        options={CANTON_STATUS_OPTIONS}
        value={status}
        onChange={(next) => {
          onChange({ status: next === 'all' ? [] : [next] });
        }}
      />
      <PeriodFilter
        from={value.from}
        to={value.to}
        fromLabel="Modifiée depuis le"
        toLabel="Modifiée jusqu’au"
        onChange={onChange}
      />
      {canReset ? (
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            onChange(CLEAR_CANTON_FIX_FILTERS);
          }}
        >
          <X aria-hidden="true" />
          Réinitialiser
        </Button>
      ) : null}
    </div>
  );
}
